import { Controller } from "@hotwired/stimulus"

// Shows a synced loupe at the cursor's normalized position across three panels
// (reference original + two candidates) so voters can peek at 100% crops.
//
// Targets:
//   trigger  – one or more image wrappers. Pointer movement inside these
//              panels drives the loupe position.
//   lens     – one or more loupe panels. Each lens has data attributes:
//                data-pixel-peek-src   (fully-resolved URL)
//                data-pixel-peek-natural-width, data-pixel-peek-natural-height
//   lensRow  – the container that holds the lens elements (hidden until first
//              interaction).
export default class PixelPeekController extends Controller {
  static targets = ["trigger", "lens", "lensRow"]

  declare triggerTargets: HTMLElement[]
  declare lensTargets: HTMLElement[]
  declare lensRowTarget: HTMLElement
  declare hasLensRowTarget: boolean

  // Shared reference dimensions — all lenses use these for backgroundSize so
  // the same normalized cursor position maps to the same physical region in
  // every lens. Max across lenses = upscale's native size; lower-res sources
  // get CSS-upscaled (blurry — the quality delta we want to expose).
  private refW = 0
  private refH = 0

  // One overlay circle per trigger, synced so the user sees which region of
  // the source image each lens is peeking at.
  private indicators: HTMLElement[] = []

  // Which trigger the current touch-drag started on. While non-null, document
  // touchmove events update the lens position relative to this trigger.
  private activeTrigger: HTMLElement | null = null

  // Defer the actual loupe image fetches until the user first peeks. Loading
  // three full-resolution sources on every matchup, even when the voter never
  // peeks, was wasteful and made the strip look "ready" before it was.
  private lensesMounted = false

  connect() {
    this.lensTargets.forEach(lens => {
      lens.style.backgroundRepeat = "no-repeat"
      const w = Number(lens.dataset.pixelPeekNaturalWidth ?? 0)
      const h = Number(lens.dataset.pixelPeekNaturalHeight ?? 0)
      if (w > this.refW) this.refW = w
      if (h > this.refH) this.refH = h
    })

    this.triggerTargets.forEach(el => {
      if (getComputedStyle(el).position === "static") {
        el.style.position = "relative"
      }
      // touch-action:none on the trigger and every descendant prevents the
      // browser from claiming horizontal swipes for scroll/back-gesture.
      // Setting only on the figure wasn't enough — horizontal drags were
      // being eaten by <img>/<picture>'s default touch-action.
      el.style.touchAction = "none"
      el.querySelectorAll<HTMLElement>("*").forEach(child => {
        child.style.touchAction = "none"
      })

      const indicator = document.createElement("div")
      indicator.className = "pixel-peek__indicator"
      el.appendChild(indicator)
      this.indicators.push(indicator)

      // Mouse path: lightweight listeners on the trigger itself.
      el.addEventListener("mousemove",  this.handleMouseMove)
      el.addEventListener("mouseleave", this.handleMouseLeave)
      // Touch path: listen for the start on the trigger, then attach the
      // drag handlers to `document` so the finger can leave the figure
      // without us losing the drag. This is the classic "most reliable on
      // mobile" pattern — sidesteps browser-specific quirks with touchmove
      // dispatch and pointer capture.
      el.addEventListener("touchstart", this.handleTouchStart, { passive: false })
    })
  }

  disconnect() {
    this.triggerTargets.forEach(el => {
      el.removeEventListener("mousemove",  this.handleMouseMove)
      el.removeEventListener("mouseleave", this.handleMouseLeave)
      el.removeEventListener("touchstart", this.handleTouchStart)
    })
    this.stopTouchDrag()
    this.indicators.forEach(ind => ind.remove())
    this.indicators = []
  }

  // ── Mouse ──

  private handleMouseMove = (e: MouseEvent) => {
    const target = e.currentTarget as HTMLElement
    this.updateFromPoint(e.clientX, e.clientY, target)
  }

  private handleMouseLeave = () => {
    this.hide()
  }

  // ── Touch ──

  private handleTouchStart = (e: TouchEvent) => {
    e.preventDefault()
    this.activeTrigger = e.currentTarget as HTMLElement
    const touch = e.touches[0]
    if (!touch) return
    this.updateFromPoint(touch.clientX, touch.clientY, this.activeTrigger)

    document.addEventListener("touchmove",   this.handleDocTouchMove,  { passive: false })
    document.addEventListener("touchend",    this.handleDocTouchEnd)
    document.addEventListener("touchcancel", this.handleDocTouchEnd)
  }

  private handleDocTouchMove = (e: TouchEvent) => {
    if (!this.activeTrigger) return
    e.preventDefault()
    const touch = e.touches[0]
    if (!touch) return
    this.updateFromPoint(touch.clientX, touch.clientY, this.activeTrigger)
  }

  private handleDocTouchEnd = () => {
    // Don't hide on touchend — leave the loupe pinned at the last touch
    // position so the voter can sit back and inspect A vs. B crops without
    // keeping a finger on the screen. The next touchstart relocates it; a
    // new matchup resets the controller.
    this.stopTouchDrag()
  }

  private stopTouchDrag() {
    this.activeTrigger = null
    document.removeEventListener("touchmove",   this.handleDocTouchMove)
    document.removeEventListener("touchend",    this.handleDocTouchEnd)
    document.removeEventListener("touchcancel", this.handleDocTouchEnd)
  }

  // ── Shared ──

  // Lazily fetch and assign the full-resolution background images for each
  // lens. Called on the first peek interaction so a voter who scrolls past
  // the strip never pays the bandwidth cost.
  private mountLenses() {
    if (this.lensesMounted) return
    this.lensesMounted = true
    this.lensTargets.forEach(lens => {
      const src = lens.dataset.pixelPeekSrc
      if (!src) return
      lens.classList.add("is-loading")
      const image = new Image()
      image.onload = () => {
        lens.style.backgroundImage = `url(${JSON.stringify(src)})`
        lens.classList.remove("is-loading")
      }
      image.onerror = () => lens.classList.remove("is-loading")
      image.src = src
    })
  }

  private updateFromPoint(clientX: number, clientY: number, target: HTMLElement) {
    const rect = target.getBoundingClientRect()
    if (!rect.width || !rect.height) return
    const nx = (clientX - rect.left) / rect.width
    const ny = (clientY - rect.top)  / rect.height
    this.updateLenses(nx, ny)
  }

  private hide() {
    if (this.hasLensRowTarget) {
      this.lensRowTarget.classList.remove("is-peeking")
    }
    this.indicators.forEach(ind => ind.classList.remove("is-visible"))
  }

  private updateLenses(nx: number, ny: number) {
    const clamp = (v: number) => Math.max(0, Math.min(1, v))
    const x = clamp(nx)
    const y = clamp(ny)

    if (this.hasLensRowTarget) {
      this.lensRowTarget.classList.add("is-peeking")
    }

    this.mountLenses()

    const refW = this.refW
    const refH = this.refH
    if (!refW || !refH) return

    // Lens viewport in source-pixel units (all lenses share a size).
    const firstLens = this.lensTargets[0]
    const lensW = firstLens?.clientWidth ?? 0
    const lensH = firstLens?.clientHeight ?? 0

    // Aspect-ratio correction: trigger uses object-cover which crops the
    // longer dimension. Translate cursor coords → natural image coords before
    // positioning the lens background.
    //
    // IMPORTANT: pick the first *visible* trigger. On mobile the reference
    // <figure> is `display:none` (the ref is rendered as a small chip
    // instead) but still carries data-pixel-peek-target="trigger", so it's
    // in triggerTargets[0]. Using its clientWidth would be 0, which
    // collapses visibleFractionX to 0, pins imgX to 0.5, and makes the lens
    // only track Y. It also sends rawDiameterX to Infinity, producing an
    // invalid `translate3d(-Infinitypx, ...)` that the browser drops —
    // leaving the indicator stuck at top-left.
    const measuredTrigger = this.triggerTargets.find(
      t => t.clientWidth > 0 && t.clientHeight > 0
    )
    const triggerAspect = measuredTrigger
      ? measuredTrigger.clientWidth / measuredTrigger.clientHeight
      : 1
    const imageAspect = refW / refH
    let visibleFractionX = 1
    let visibleFractionY = 1
    let cropFractionX = 0
    let cropFractionY = 0
    if (imageAspect > triggerAspect) {
      visibleFractionX = triggerAspect / imageAspect
      cropFractionX = (1 - visibleFractionX) / 2
    } else if (imageAspect < triggerAspect) {
      visibleFractionY = imageAspect / triggerAspect
      cropFractionY = (1 - visibleFractionY) / 2
    }
    const imgX = cropFractionX + x * visibleFractionX
    const imgY = cropFractionY + y * visibleFractionY

    this.lensTargets.forEach(lens => {
      const thisLensW = lens.clientWidth
      const thisLensH = lens.clientHeight
      if (!thisLensW || !thisLensH) return

      const bgX = -(imgX * refW - thisLensW / 2)
      const bgY = -(imgY * refH - thisLensH / 2)
      lens.style.backgroundSize     = `${refW}px ${refH}px`
      lens.style.backgroundPosition = `${bgX}px ${bgY}px`
    })

    // Indicator circle: represents the capture region, but clamped to a sane
    // minimum so it's always visible (raw ratio drops below 1 % for high-res
    // upscales, producing a sub-pixel dot that's impossible to see).
    const MIN_INDICATOR = 48
    this.indicators.forEach((ind, i) => {
      const trigger = this.triggerTargets[i]
      if (!trigger) return
      const triggerW = trigger.clientWidth
      const triggerH = trigger.clientHeight
      if (!triggerW || !triggerH) return

      const rawDiameterX = lensW && refW
        ? (lensW / refW) / visibleFractionX * triggerW
        : MIN_INDICATOR
      const rawDiameterY = lensH && refH
        ? (lensH / refH) / visibleFractionY * triggerH
        : MIN_INDICATOR
      const diameterX = Math.max(rawDiameterX, MIN_INDICATOR)
      const diameterY = Math.max(rawDiameterY, MIN_INDICATOR)
      // Position via transform (GPU-friendly; avoids any mobile quirks with
      // rapid updates to `left`).
      const translateX = x * triggerW - diameterX / 2
      const translateY = y * triggerH - diameterY / 2
      ind.style.width     = `${diameterX}px`
      ind.style.height    = `${diameterY}px`
      ind.style.left      = "0"
      ind.style.top       = "0"
      ind.style.transform = `translate3d(${translateX}px, ${translateY}px, 0)`
      ind.classList.add("is-visible")
    })
  }
}
